import { useEffect, useId, useState } from "react";
import type { AutopilotView, BalanceMoney, TradingResult } from "@auto-pools/core";
import { formatMoney, formatPercent2, formatSignedAmount, signTone } from "../lib/format";
import { AutopilotActionButton } from "./AutopilotPill";
import { BalanceSummary, type BalanceSummaryProps } from "./BalanceSummary";
import { PortfolioChart, PortfolioChartButton } from "./PortfolioChart";
import { readPreferences, writePreferences, type ChartPreferences } from "./portfolioChartState";
import {
  poolsEmptyReason,
  spotCell,
  staleness,
  summaryRowState,
  valuationTimes,
} from "./summaryRowState";

/**
 * A status poll only re-renders this row when it settles (`useResource.ts`); while one is pending
 * past a stale bound, nothing else forces a redraw. This ticks `now` on its own so the marker still
 * appears from render alone, without waiting on network activity.
 */
const STALE_CHECK_INTERVAL_MS = 15_000;

/**
 * "+$5.00 (0.50%)": the percent part only while known; "—" while the result is unknown. No stale
 * marker here: one that appears for the moment a returning tab re-reads its valuation widens the
 * cells, wraps the row and makes the whole list jump (the user's call, 29.10); the chart keeps them.
 */
function Result({ result }: { result: TradingResult | null }) {
  if (!result?.result) return <span className="num summary-result">—</span>;
  const money = formatSignedAmount(result.result);
  const tone = signTone(result.result);
  return (
    <span className={`num summary-result ${tone}`}>
      {result.percent === null ? money : `${money} (${formatPercent2(result.percent)})`}
    </span>
  );
}

function usd(value: BalanceMoney | null | undefined) {
  return value?.usd === null || value?.usd === undefined ? "—" : formatMoney(value.usd);
}

/**
 * My positions' one summary row (design 58): Total (the balance popover's trigger), In pools,
 * In spot and Free cash, each a server figure, with the all-time result of its engine beside it.
 * The autopilot's state is the dot plus a second line that appears only when there is engine news;
 * a stale valuation is marked only in the chart, so it never changes the row's height.
 */
export function PositionsSummaryRow({
  view,
  poolCount,
  balance,
  onOpenAutopilot,
}: {
  view: AutopilotView | null;
  /** Pool positions in the list; undefined while the first positions read is pending. */
  poolCount: number | undefined;
  balance: Omit<BalanceSummaryProps, "label" | "result" | "spot">;
  onOpenAutopilot?: () => void;
}) {
  const times = valuationTimes(view);
  const ticking = times.some((at) => at !== null);
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    if (!ticking) return;
    const timer = window.setInterval(() => {
      setNow(Date.now());
    }, STALE_CHECK_INTERVAL_MS);
    return () => {
      window.clearInterval(timer);
    };
  }, [ticking]);
  const [chart, setChart] = useState<ChartPreferences>(readPreferences);
  const chartId = useId();
  const updateChart = (next: ChartPreferences) => {
    setChart(next);
    writePreferences(next);
  };
  const stale = staleness(view, now);
  const state = summaryRowState(view);
  const results = view?.results ?? null;
  const split = balance.data?.split ?? null;
  // The Total pill's stale rule (BalanceSummary): a remembered summary after a failed read is stale.
  const balanceStale =
    (balance.data?.balance ?? null) !== null &&
    (balance.positionsError ||
      balance.error !== null ||
      (balance.data?.lastRefresh != null && !balance.data.lastRefresh.ok));
  const spot = view?.spot ?? null;
  const invested = results?.total.invested ?? null;
  const pools = poolCount === undefined || poolCount > 0 ? null : poolsEmptyReason(view);
  const spotLabel = spot ? spotCell(spot) : null;
  return (
    <>
      <div
        className={`panel autopilot-status-strip summary-row ${state.tone}${chart.open ? " chart-open" : ""}`}
      >
        <div className="summary-row-main">
          <div className="summary-cell summary-total">
            <span className={`status-dot ${state.tone}`} aria-hidden="true" />
            <BalanceSummary
              {...balance}
              spot={spot}
              label={invested === null ? "Total" : `Total · invested ${formatMoney(invested)}`}
              result={<Result result={results?.total ?? null} />}
            />
          </div>
          <span className="summary-divider" aria-hidden="true" />
          <div className="summary-cell summary-pools">
            <span className="summary-label">
              {poolCount === undefined
                ? "In pools"
                : poolCount === 0
                  ? "In pools · none"
                  : `In pools · ${String(poolCount)}`}
            </span>
            <span className="summary-figure">
              {poolCount === 0 ? (
                <>
                  <span className="num summary-value empty">—</span>
                  {pools !== null && <span className="summary-reason">{pools}</span>}
                </>
              ) : (
                <>
                  <span className="num summary-value">{usd(split?.inPools)}</span>
                  <span className="summary-result-line">
                    <Result result={results?.pools ?? null} />
                  </span>
                </>
              )}
            </span>
          </div>
          {spot && spotLabel && (
            <div className="summary-cell summary-spot">
              <span className="summary-label">{spotLabel.label}</span>
              <span className="summary-figure">
                {spotLabel.emptyReason !== null ? (
                  <>
                    <span className="num summary-value empty">—</span>
                    <span className="summary-reason">{spotLabel.emptyReason}</span>
                  </>
                ) : (
                  <>
                    <span className="num summary-value">{usd(split?.inSpot)}</span>
                    <span className="summary-result-line">
                      <Result result={results?.spot ?? null} />
                    </span>
                  </>
                )}
              </span>
            </div>
          )}
          <div className="summary-cell summary-free">
            <span className="summary-label">Free cash</span>
            <span className="summary-figure">
              <span className="num summary-value">{usd(split?.freeCash)}</span>
            </span>
          </div>
          <PortfolioChartButton
            open={chart.open}
            panelId={chartId}
            onToggle={() => {
              updateChart({ ...chart, open: !chart.open });
            }}
          />
          {onOpenAutopilot && (
            <button
              type="button"
              className="button ghost small summary-row-open"
              onClick={onOpenAutopilot}
            >
              Open Autopilot →
            </button>
          )}
        </div>
        {state.lines.length > 0 && (
          <div className="summary-row-note">
            {state.lines.map((line) => (
              <span key={line}>{line}</span>
            ))}
          </div>
        )}
        {
          // ≤760 px only (design 47, section 10): the header pill has no room for its button there,
          // so the same contextual action moves here, next to a full "Open autopilot" button.
          view && onOpenAutopilot && (
            <div className="autopilot-status-strip-actions">
              <AutopilotActionButton view={view} onOpen={onOpenAutopilot} className="button" />
              <button type="button" className="button primary" onClick={onOpenAutopilot}>
                Open autopilot
              </button>
            </div>
          )
        }
      </div>
      {chart.open && (
        <PortfolioChart
          id={chartId}
          preferences={chart}
          onPreferences={updateChart}
          results={results}
          split={split}
          spotLeg={spot !== null}
          stale={{
            total: stale.total,
            pools: stale.pools,
            spot: stale.spot,
            value: balanceStale,
          }}
        />
      )}
    </>
  );
}
